import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
export default (props) => {
    const id = React.useId();
    const createMarkers = React.useRef([]).current;
    const createMarker = (i) => { var _a; return (_a = createMarkers[i]) !== null && _a !== void 0 ? _a : (createMarkers[i] = Symbol(`create-${i}`)); };
    const inputRef = React.useRef(null);
    const optionsRef = React.useRef(null);
    const [stateFilter, setFilter] = React.useState('');
    const [stateFocused, setFocused] = React.useState(false);
    const [stateHovered, setHovered] = React.useState(props.emptyValue === undefined ? null : props.emptyValue);
    const empty = (props.emptyValue === undefined ? null : props.emptyValue);
    const filter = stateFilter.trim().toLocaleLowerCase();
    const createText = stateFilter.trim();
    const matches = React.useMemo(() => (props.items || [])
        .map((x) => ({ 'value': props.valuef(x), 'title': props.titlef(x), 'search': (props.searchf ? props.searchf(x) : props.titlef(x)).toLocaleLowerCase() }))
        .filter((x) => x.search.includes(filter))
        // Matches that start with what was typed come first; the rest keep their order.
        .sort((a, b) => Number(b.search.startsWith(filter)) - Number(a.search.startsWith(filter))), [props.items, props.titlef, props.searchf, props.valuef, filter]);
    const canCreate = !!createText && !matches.some((x) => x.search === createText.toLocaleLowerCase());
    const filtered = React.useMemo(() => ([{ 'value': empty, 'title': props.emptyTitle || 'Select...' }])
        .concat(matches)
        .concat(createText ? (props.creates || []).flatMap((x, i) => (x.available ? x.available(createText) : canCreate) ? [{ 'value': createMarker(i), 'title': x.title(createText) }] : []) : []), [empty, props.emptyTitle, matches, canCreate, props.creates, createText]);
    const optionValues = JSON.stringify(filtered.map((x) => String(x.value)));
    React.useEffect(() => {
        // Index 0 is the empty placeholder, so index 1 is the first real match, or the first create entry when nothing matches.
        setHovered(filter && filtered.length > 1 ? filtered[1].value : empty);
    }, [optionValues, empty, filter]);
    React.useEffect(() => {
        const parent = optionsRef.current;
        if (!parent || !stateFocused) {
            return;
        }
        const target = parent.getElementsByClassName('hovered')[0];
        if (!target) {
            return;
        }
        if (target.offsetTop + target.offsetHeight > parent.scrollTop + parent.clientHeight) {
            parent.scrollTo({ 'top': target.offsetTop + target.offsetHeight - parent.clientHeight, 'behavior': 'smooth' });
        }
        if (target.offsetTop < parent.scrollTop) {
            parent.scrollTo({ 'top': target.offsetTop, 'behavior': 'smooth' });
        }
    }, [stateHovered, stateFocused]);
    const blur = () => {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        setFocused(false);
    };
    const choose = (value) => {
        var _a, _b;
        if (typeof value === 'symbol') {
            (_b = (_a = props.creates) === null || _a === void 0 ? void 0 : _a[createMarkers.indexOf(value)]) === null || _b === void 0 ? void 0 : _b.onCreate(createText);
        }
        else {
            props.onChange(value);
        }
        blur();
    };
    const selected = (props.value !== null && props.value !== undefined)
        ? props.items.find((x) => props.valuef(x) === props.value)
        : null;
    const floating = stateFocused || !!props.value;
    return _jsxs("div", Object.assign({ className: `form-group floating-label w-100 position-relative ${props.small && 'form-group-sm'} ${!props.inline && 'mb-3'} ${props.inline && 'mb-0'} ${!!props.prepend && 'prepend'} ${!!props.append && 'append'}` }, (props.required ? { 'required': true } : {}), { onBlur: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
                blur();
            }
        }, children: [_jsx("label", { htmlFor: id, className: floating ? 'floating' : '', children: props.children }), _jsxs("div", { className: `input-group ${props.small && 'input-group-sm'}`, children: [!!props.prepend && _jsx("div", { className: "input-group-text", children: props.prepend }), _jsx("input", { autoComplete: "off", ref: inputRef, id: id, value: stateFocused ? stateFilter : selected ? props.titlef(selected) : '', type: "text", className: `form-control ${props.small && 'form-control-sm'} ${props.large && 'form-control-md'} ${!!props.error && 'is-invalid'}`, disabled: props.disabled, onFocus: () => {
                            setFocused(true);
                            setFilter('');
                            setHovered(empty);
                        }, onChange: (e) => setFilter(e.target.value), onKeyDown: (e) => {
                            if (['Enter', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                                e.preventDefault();
                            }
                        }, onKeyUp: (e) => {
                            const hoveredIndex = filtered.findIndex((x) => x.value === stateHovered);
                            switch (e.key) {
                                case 'Enter':
                                    choose(stateHovered);
                                    break;
                                case 'Escape':
                                    blur();
                                    break;
                                case 'ArrowUp':
                                    if (hoveredIndex > 0) {
                                        setHovered(filtered[hoveredIndex - 1].value);
                                    }
                                    break;
                                case 'ArrowDown':
                                    if (hoveredIndex >= 0 && hoveredIndex < filtered.length - 1) {
                                        setHovered(filtered[hoveredIndex + 1].value);
                                    }
                                    break;
                                default:
                                    break;
                            }
                        } }), _jsx("ul", { ref: optionsRef, className: `position-absolute w-100 rounded-bottom bg-white list-unstyled p-1 ${!stateFocused && 'd-none'}`, style: { 'maxHeight': '250px', 'overflowX': 'auto', 'top': '100%', 'left': 0, 'zIndex': 999 }, children: filtered.map((x, i) => _jsx("li", { role: "button", className: `p-1 pointer ${props.value === x.value ? 'text-primary bg-light selected' : ''} ${stateHovered === x.value ? 'bg-light fw-bold hovered' : ''} ${typeof x.value === 'symbol' ? 'text-primary' : ''}`, onMouseMove: () => setHovered(x.value), onMouseDown: (e) => e.preventDefault(), onClick: () => {
                                setHovered(x.value);
                                choose(x.value);
                            }, tabIndex: -1, children: x.title }, `${x.title}-${i}`)) }), !!props.append && _jsx("div", { className: "input-group-text", children: props.append })] }), !!props.extra && _jsx("div", { className: "text-secondary small", children: props.extra }), !!props.error && _jsx("div", { className: "text-danger small", children: props.error })] }));
};
